// Arrow-key movement around the time-entry grid.
//
// The grid is one <table> with a <tbody> per day, so walking `tr.te-row` in
// document order crosses day boundaries for free. Columns are matched by the
// cell's position in the row rather than by counting inputs, because a row
// whose task is a closed file renders text instead of a field and would
// otherwise throw the column alignment out by one.

const ARROWS = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

const focusableIn = (cell) => cell?.querySelector("input.te-in:not([disabled])") || null;

// Whether this keystroke is the grid's to act on, or belongs to whatever the
// user is currently doing inside the field.
export function shouldNavigate(el, key) {
  if (key === "ArrowUp" || key === "ArrowDown") {
    // A ComboField with its list showing owns Up/Down -- that is how you pick
    // a code or a year. Esc closes it and hands the keys back to the grid.
    return el.getAttribute("aria-expanded") !== "true";
  }

  // Left/Right move the caret first and only fall through to the grid once the
  // caret is already against that edge, so typing a description is untouched.
  let start, end;
  try {
    start = el.selectionStart;
    end = el.selectionEnd;
  } catch (_) {
    // Firefox throws on selectionStart for type=number.
    return true;
  }
  if (start == null) return true;          // number inputs report null here
  if (start !== end) return false;         // mid-selection: leave it alone
  return key === "ArrowLeft" ? start === 0 : start === (el.value || "").length;
}

export function nextCell(table, current, key) {
  const cell = current.closest("td");
  const row = current.closest("tr.te-row");
  if (!cell || !row || !table) return null;

  if (key === "ArrowLeft" || key === "ArrowRight") {
    const cells = Array.from(row.cells);
    const step = key === "ArrowLeft" ? -1 : 1;
    for (let i = cell.cellIndex + step; i >= 0 && i < cells.length; i += step) {
      const found = focusableIn(cells[i]);
      if (found) return found;
    }
    return null;
  }

  const rows = Array.from(table.querySelectorAll("tr.te-row"));
  const step = key === "ArrowUp" ? -1 : 1;
  for (let i = rows.indexOf(row) + step; i >= 0 && i < rows.length; i += step) {
    // Skip a row that has nothing focusable in this column (a locked line)
    // rather than stopping on it.
    const found = focusableIn(rows[i].cells[cell.cellIndex]);
    if (found) return found;
  }
  return null;
}

// Everything Tab may land on, in document order. Excludes a cell that opts out
// with tabIndex -1 -- the Year End column, which is derived from the task, or
// from the client and date, and so is never something to stop and fill in.
const TAB_STOPS =
  'input.te-in:not([disabled]):not([tabindex="-1"]), button.te-x, button.te-sb, button.te-add-btn';

// Tab, driven by the grid instead of by the browser.
//
// Native Tab works out its destination from the DOM as it stands DURING the
// keystroke, before React has applied what that same keystroke changed. The
// Tab that commits a task is the bad case: it can be sent to a cell that is
// about to stop accepting focus, and focus then falls to <body>, so the next
// Tab starts again from the top of the page rather than the next column.
// Choosing the destination here and focusing it ourselves removes the race:
// the order is Code, Client, Task, Type, Hrs, Description, delete -- Year End
// is read, not filled in.
//
// Falls through to the browser at either end of the grid, or focus could never
// leave it.
function handleTab(e) {
  const el = e.target;
  if (!el.classList || !el.classList.contains("te-in")) return;
  const stops = Array.from(e.currentTarget.querySelectorAll(TAB_STOPS));
  const here = stops.indexOf(el);
  if (here === -1) return;
  const next = stops[here + (e.shiftKey ? -1 : 1)];
  if (!next) return;
  e.preventDefault();
  next.focus();
}

// Attach to the <table>. Field-level handlers run first, so ComboField has
// already had its say by the time this sees the key -- which is what lets Tab
// commit a highlighted option and still land where we send it.
export function onGridKeyDown(e) {
  if (e.key === "Tab") { handleTab(e); return; }
  if (!ARROWS.includes(e.key)) return;
  const el = e.target;
  if (!el.classList || !el.classList.contains("te-in")) return;
  if (!shouldNavigate(el, e.key)) return;

  const target = nextCell(e.currentTarget, el, e.key);
  if (!target) return;
  e.preventDefault();
  // Focus only -- never select. The grid's rule is that arriving in a field
  // highlights nothing, so the next keystroke cannot silently replace a value.
  // The one exception is Hrs, which selects itself on focus (selectOnFocus.js)
  // at the owner's request so a saved 0.00 need not be backspaced out.
  target.focus();
}
